import { Component, Input, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ResponsePortalApartmentDto } from '../../../../../../../generated/apex-rest';
import { PortalService } from '../../../../../../portal/src/app/portal.service';
import { Apartment } from '../../../models/apartment';
import { Profile } from '../../../models/profile';
import { Request } from '../../../models/request';
import { CollectionResponse } from '../../../utils/types';
import { RequestConsentDialogComponent } from '../consent-dialog/request-consent-dialog.component';
import {
  RequestConsentDialogData,
  RequestConsentDialogReturnData,
} from '../consent-dialog/request-consent-dialog.types';
import { RequestDialogComponent } from '../dialog/request-dialog.component';
import { RequestDialogData, RequestDialogReturnData } from '../dialog/request-dialog.types';

@Component({
  selector: 'apex-request-list',
  templateUrl: './request-list.component.html',
})
export class RequestListComponent implements OnDestroy {
  @Input() disableRequests = false;

  requests: Request[] = [];

  limit = 100;
  page = 0;
  count = 0;

  profile: Profile;
  apartment: Apartment | ResponsePortalApartmentDto;

  private subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private portalService: PortalService,
  ) {
    this.subscription.add(
      this.route.data
        .pipe(
          map(
            (data) =>
              [data.requests, data.profile, data.apartment] as [CollectionResponse<Request>, Profile, Apartment],
          ),
          map(([res, profile, apartment]) => {
            this.page = res?.page;
            this.count = res?.count;

            this.apartment = apartment;
            this.profile = profile;

            return res?.Collection ?? [];
          }),
          map((requests) => requests.map((request) => new Request(request))),
        )
        .subscribe({
          next: (requests) => {
            this.requests = requests;
          },
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  changePage(event: PageEvent): void {
    this.page = event.pageIndex;
    this.limit = event.pageSize;

    this.updateQueryParams();
  }

  async createRequest(): Promise<void> {
    let consentedToArticleId: string;

    const {
      requiresConsentForRequests = false,
      requiresConsentForRequestsArticle = null,
      requiresExplicitConsent = false,
    } = await this.portalService.readProjectApartmentNeedConsentForRequests(
      this.apartment.ProjectId,
      this.apartment.id,
    );

    if (requiresConsentForRequests && requiresConsentForRequestsArticle) {
      consentedToArticleId = requiresConsentForRequestsArticle.id;

      const consent = await firstValueFrom(
        this.dialog
          .open<RequestConsentDialogComponent, RequestConsentDialogData, RequestConsentDialogReturnData>(
            RequestConsentDialogComponent,
            {
              data: {
                article: requiresConsentForRequestsArticle,
                requiresExplicitConsent,
              },
              panelClass: ['apex-fullscreen-dialog'],
            },
          )
          .afterClosed()
          .pipe(filter((result) => !!result)),
      );

      if (!consent?.consented) {
        return;
      }
    }

    let CustomerId: number | null = null;

    if ('Project' in this.apartment && 'CustomerId' in this.apartment.Project) {
      CustomerId = this.apartment.Project.CustomerId;
    } else if ('Project' in this.apartment && 'CompanyId' in this.apartment.Project) {
      CustomerId = this.apartment.Project.CompanyId;
    }

    if (!CustomerId) {
      throw new Error('CustomerId is required to create a request');
    }

    const requestData: Partial<Request> = {
      CustomerId,
      ProjectId: String(this.apartment.ProjectId),
      ApartmentId: String(this.apartment.id),
      ClientId: String(this.profile.id),

      consentedToArticleId,
    };

    const request = await firstValueFrom(
      this.dialog
        .open<RequestDialogComponent, RequestDialogData, RequestDialogReturnData>(RequestDialogComponent, {
          data: {
            request: new Request(requestData),
            mainObjects: this.route.snapshot.data.entities ?? [],
          },
          panelClass: ['apex-fullscreen-dialog', 'phone'],
        })
        .afterClosed()
        .pipe(filter((req) => !!req)),
    );

    if (String(request.ApartmentId) === String(this.apartment.id)) {
      this.requests.unshift(new Request(request));
    }
  }

  private updateQueryParams(): void {
    const queryParams: Params = {
      requestPage: this.page,
      requestLimit: this.limit,
    };

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      state: { skipScroll: true },
    });
  }
}
